import { removeDupes, cleanObject } from '../utils/helpers';

const discussions = (state = {
    items: [],
    fetchInfo: {
        team: {
            loading: false,
            fetched: false
        },
        project: {}
    }
}, action) => {
    if (action.error) {
        return state;
    }

    switch (action.type) {
        case 'TEAM_CHANGE':
        case 'RESET_STORE':
            return {
                items: [],
                fetchInfo: {
                    team: {
                        loading: false,
                        fetched: false
                    },
                    project: {}
                }
            };

        case 'MARK_ALL_FOR_REFETCH':
            return Object.assign({}, state, {
                items: [...state.items.map(discussion => ({
                    ...discussion,
                    shouldRefetch: true
                }))]
            });

        case 'DISCUSSIONS_FETCH_REQUESTED': {
            if (action.payload.projectId) {
                return Object.assign({}, state, {
                    fetchInfo: {
                        ...state.fetchInfo,
                        project: {
                            ...state.fetchInfo.project,
                            [action.payload.projectId]: {
                                fetched: false,
                                loading: true
                            }
                        }
                    }
                });
            }

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    team: {
                        fetched: false,
                        loading: true
                    }
                }
            });
        }

        case 'DISCUSSIONS_FETCH_SUCCEEDED': {
            if (!action.payload.discussions) {
                return state;
            }

            const items = removeDupes([...action.payload.discussions, ...state.items])

            if (action.payload.projectId) {
                return Object.assign({}, state, {
                    items,
                    fetchInfo: {
                        ...state.fetchInfo,
                        project: {
                            ...state.fetchInfo.project,
                            [action.payload.projectId]: {
                                fetched: true,
                                loading: false,
                                error: false
                            }
                        }
                    }
                });
            }

            return Object.assign({}, state, {
                items,
                fetchInfo: {
                    ...state.fetchInfo,
                    team: {
                        fetched: true,
                        loading: false,
                        error: false
                    }
                }
            });
        }

        case 'DISCUSSIONS_FETCH_FAILED': {
            if (action.payload.projectId) {
                return Object.assign({}, state, {
                    fetchInfo: {
                        ...state.fetchInfo,
                        project: {
                            ...state.fetchInfo.project,
                            [action.payload.projectId]: {
                                loading: false,
                                fetched: true,
                                error: true
                            }
                        }
                    }
                });
            }

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    team: {
                        loading: false,
                        fetched: true,
                        error: true
                    }
                }
            });
        }

        case 'DISCUSSION_CREATE_REQUESTED':
        case 'DISCUSSION_ADD_SOCKET':
            return Object.assign({}, state, {
                items: removeDupes([...state.items, action.payload.discussion])
            });

        case 'DISCUSSION_CREATE_FAILED':
            return Object.assign({}, state, {
                items: state.items.filter(discussion => discussion !== action.payload.fragment)
            });

        case 'DISCUSSION_CREATE_SUCCEEDED':
            return Object.assign({}, state, {
                items: state.items
                    .filter(discussion => discussion.id !== action.payload.discussionId)
                    .map(discussion => {
                        if (discussion === action.payload.fragment) {
                            discussion.id = action.payload.discussionId;
                            discussion.color = action.payload.color;
                        }

                        return discussion;
                    })
            });

        case 'DISCUSSION_FETCH_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items.filter(discussion => discussion.id !== action.payload.discussionId), {
                    ...(state.items.find(discussion => discussion.id === action.payload.discussionId) || {}),
                    id: action.payload.discussionId,
                    loading: true
                }]
            });

        case 'DISCUSSION_FETCH_FAILED':
            return Object.assign({}, state, {
                items: [...state.items.filter(discussion => discussion.id !== action.payload.discussionId), {
                    id: action.payload.discussionId,
                    error: true
                }]
            });

        case 'DISCUSSION_FETCH_SUCCEEDED':
            if (!action.payload?.discussion?.id) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items.filter(discussion => discussion.id !== action.payload.discussion.id), action.payload.discussion]
            });

        case 'DISCUSSION_UPDATE_REQUESTED':
        case 'DISCUSSION_UPDATE_SOCKET':
            if (!state.items.some(discussion => discussion.id === action.payload.discussionId)) {
                return Object.assign({}, state, {
                    items: [...state.items, action.payload.discussion]
                });
            }

            return Object.assign({}, state, {
                items: [...state.items].map(discussion => {
                    if (discussion.id === action.payload.discussionId) {
                        return Object.assign({}, discussion, cleanObject(action.payload.discussionProps || action.payload.discussion));
                    }

                    return discussion;
                })
            });

        case 'DISCUSSION_ADD_MEMBERS_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(discussion => {
                    if (discussion.id === action.payload.discussionId) {
                        return Object.assign({}, discussion, {
                            members: removeDupes([...discussion.members, ...action.payload.members])
                        });
                    }

                    return discussion;
                })
            });

        case 'DISCUSSION_REMOVE_MEMBERS_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(discussion => {
                    if (discussion.id === action.payload.discussionId) {
                        return Object.assign({}, discussion, {
                            members: discussion.members.filter(user => !user || !action.payload.members.includes(user.id))
                        });
                    }

                    return discussion;
                })
            });

        case 'DISCUSSION_MUTE_REQUESTED':
        case 'DISCUSSION_MUTE_SOCKET':
        case 'DISCUSSION_UNMUTE_REQUESTED':
        case 'DISCUSSION_UNMUTE_SOCKET': {
            const actionIsMute = action.type === 'DISCUSSION_MUTE_REQUESTED' || action.type === 'DISCUSSION_MUTE_SOCKET';

            return Object.assign({}, state, {
                items: [...state.items].map(discussion => {
                    if (discussion.id === action.payload.discussionId) {
                        if (discussion.members.some(user =>
                                user &&
                                user.id === action.payload.userId &&
                                (
                                    (actionIsMute && user.is_muted) ||
                                    (!actionIsMute && !user.is_muted)
                                )
                        )) {
                            return discussion;
                        }

                        return Object.assign({}, discussion, {
                            members: [...discussion.members].map(user => {
                                if (user && user.id === action.payload.userId) {
                                    return {
                                        ...user,
                                        is_muted: actionIsMute
                                    }
                                }

                                return user;
                            })
                        });
                    }

                    return discussion;
                })
            });
        }

        case 'PROJECT_INVITE_USERS_SUCCEEDED':
            return Object.assign({}, state, {
                items: [...state.items].map(discussion => {
                    if (discussion.project === action.payload.projectId) {
                        return Object.assign({}, discussion, {
                            members: removeDupes([...discussion.members, action.payload.users.map(userId => ({
                                id: userId,
                                is_owner: false,
                                is_muted: false,
                                joined: false
                            }))])
                        });
                    }

                    return discussion;
                })
            });

        // case 'USER_INVITE_CANCEL_SOCKET':
        // case 'USER_REMOVE_REQUESTED':
        // case 'TEAM_USER_REMOVE_SOCKET':
        //     return Object.assign({}, state, {
        //         items: [...state.items].map(discussion => {
        //             if ((discussion.members.some(user => user.id === action.payload.userId) || discussion.is_general) && (!action.payload.projectId || action.payload.projectId === discussion.project)) {
        //                 return Object.assign({}, discussion, {
        //                     members: discussion.members.filter(user => !user || user.id !== action.payload.userId)
        //                 });
        //             }

        //             return discussion;
        //         })
        //     });

        case 'PROJECT_REMOVE_USER_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(discussion => {
                    if (discussion.project === action.payload.projectId) {
                        return Object.assign({}, discussion, {
                            members: discussion.members.filter(user => !user || user.id !== action.payload.userId)
                        });
                    }

                    return discussion;
                })
            });

        case 'DISCUSSION_LEAVE_REQUESTED':
        case 'DISCUSSION_KICK_SOCKET':
        case 'DISCUSSION_REMOVE_SUCCEEDED':
        case 'DISCUSSION_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: state.items.filter(discussion => discussion.id !== action.payload.discussionId)
            });

        case 'DISCUSSION_LEAVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(discussion => {
                    if (discussion.id === action.payload.discussionId) {
                        return Object.assign({}, discussion, {
                            members: discussion.members.filter(user => !user || user.id !== action.payload.userId)
                        });
                    }

                    return discussion;
                })
            });

        default:
            return state;
    }
}

export default discussions;
